// src/redux/slices/membership.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import axiosInstance from "../../utils/axios";

export interface Membership {
  id: number;
  type: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string | null;
  associationId: number;
  userId: number;
}

interface MembershipState {
  memberships: Membership[];
  membership?: Membership;
  status: "idle" | "loading" | "failed";
}

const initialState: MembershipState = {
  memberships: [],
  membership: undefined,
  status: "idle",
};

export const fetchMembershipsByAssociation = createAsyncThunk(
  "membership/fetchMembershipsByAssociation",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/memberships/association/${associationId}`
    );
    return response.data;
  }
);

export const fetchMembershipById = createAsyncThunk(
  "membership/fetchMembershipById",
  async (id: number) => {
    const response = await axiosInstance.get(`/memberships/${id}`);
    return response.data;
  }
);

const slice = createSlice({
  name: "membership",
  initialState,
  reducers: {
    setMemberships(state, action) {
      state.memberships = action.payload;
    },
    setMembership(state, action) {
      state.membership = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembershipsByAssociation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMembershipsByAssociation.fulfilled, (state, action) => {
        state.status = "idle";
        state.memberships = action.payload;
      })
      .addCase(fetchMembershipsByAssociation.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(fetchMembershipById.fulfilled, (state, action) => {
        state.membership = action.payload;
      });
  },
});

export function createMembership(values: Partial<Membership>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.post(`/memberships`, values);
    dispatch(fetchMembershipsByAssociation(values.associationId!));
    return response.data;
  };
}

export function updateMembership(id: number, values: Partial<Membership>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosInstance.put(`/memberships/${id}`, values);
    dispatch(fetchMembershipsByAssociation(values.associationId!));
    return response.data;
  };
}

export function deleteMembership(id: number, associationId: number) {
  return async (dispatch: AppDispatch) => {
    await axiosInstance.delete(`/memberships/${id}`);
    dispatch(fetchMembershipsByAssociation(associationId));
  };
}

export const { setMemberships, setMembership } = slice.actions;
export default slice.reducer;
